import { isAxiosError } from 'axios';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import type { WorkspaceCustomFontDto } from '@/api/recipes';

/** Matches GET /v1/workspaces/{workspaceId}/fonts. */
export async function getWorkspaceFonts(workspaceId: string): Promise<WorkspaceCustomFontDto[]> {
  const response = await safeAxios.get<WorkspaceCustomFontDto[]>(API_ROUTES.workspaces.fonts({ workspaceId }));
  return response.data;
}

export type FontUploadFailureDetail = {
  code: string;
  reason: string;
};

/** Matches POST /v1/workspaces/{workspaceId}/fonts. */
export type UploadWorkspaceFontsResponse = {
  uploadedFonts: WorkspaceCustomFontDto[];
  failedToUpload: Array<Record<string, FontUploadFailureDetail>>;
};

const FONT_UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Uploads font parts for a workspace. HTTP 400 with this response shape is returned to the caller;
 * other failures are thrown.
 */
export async function uploadWorkspaceFonts(workspaceId: string, files: File[]): Promise<UploadWorkspaceFontsResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('file', file);
  }

  try {
    const response = await safeAxios.post<UploadWorkspaceFontsResponse>(
      API_ROUTES.workspaces.fonts({ workspaceId }),
      formData,
      { timeout: FONT_UPLOAD_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    if (isAxiosError<UploadWorkspaceFontsResponse>(error) && isUploadWorkspaceFontsResponse(error.response?.data)) {
      return error.response.data;
    }
    throw error;
  }
}

/** Removes every face of one family. `fontFamilyKey` is sent as a query parameter. */
export async function deleteWorkspaceFontFamily(workspaceId: string, fontFamilyKey: string): Promise<void> {
  await safeAxios.delete(API_ROUTES.workspaces.fonts({ workspaceId }), {
    params: { fontFamilyKey },
  });
}

function isUploadWorkspaceFontsResponse(data: unknown): data is UploadWorkspaceFontsResponse {
  if (typeof data !== 'object' || data == null) {
    return false;
  }
  if (!('uploadedFonts' in data) || !('failedToUpload' in data)) {
    return false;
  }
  return Array.isArray(data.uploadedFonts) && Array.isArray(data.failedToUpload);
}
